import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookMarked, MapPin, ShieldCheck, AlertTriangle, UserCheck } from "lucide-react";

const VER_CLS = {
  Verified: "bg-teal-50 text-teal-700 border-teal-200",
  "Pending verification": "bg-amber-50 text-amber-700 border-amber-200",
  "Citation conflict": "bg-red-50 text-red-700 border-red-200",
};

export function ClaimSources({ claim, compact = false }) {
  const [open, setOpen] = useState(false);
  if (!claim) return null;
  return (
    <>
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid={`claim-cite-${claim.uid}`}
        title="Open supporting source passages"
        className={`inline-flex items-center gap-1 font-mono uppercase tracking-wide rounded-full border transition-colors ${compact
          ? "text-[8.5px] px-1.5 py-px bg-slate-800 text-teal-300 border-slate-700 hover:border-teal-500"
          : "text-[9px] px-2 py-0.5 bg-white text-teal-700 border-teal-200 hover:bg-teal-50"}`}>
        <BookMarked className="w-2.5 h-2.5" />{claim.eligible_passage_count}/{claim.passage_count} sources
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid={`claim-sources-modal-${claim.uid}`} aria-describedby={`claim-sources-desc-${claim.uid}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-base pr-6 leading-snug">Source traceability — {claim.uid}</DialogTitle>
          </DialogHeader>
          <p id={`claim-sources-desc-${claim.uid}`} className="text-xs text-slate-600 leading-snug">{claim.label}</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400">{claim.location} · {claim.eligible_passage_count} eligible of {claim.passage_count} passages</p>
          <div className="space-y-2.5 max-h-[55vh] overflow-y-auto pr-1">
            {claim.passages.map((p) => (
              <div key={p.passage_id} className={`border rounded-lg p-3 ${p.eligible ? "border-slate-200 bg-white" : "border-amber-300 bg-amber-50/50"}`} data-testid={`passage-${p.passage_id}`}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-[9px] text-slate-400">{p.passage_id}</span>
                  <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">{p.kind}</Badge>
                  <Badge variant="outline" className={`text-[9px] ${VER_CLS[p.verification_status] || "bg-slate-50 text-slate-500 border-slate-200"}`}>{p.verification_status}</Badge>
                  {!p.eligible && <Badge variant="outline" className="text-[9px] bg-amber-100 text-amber-700 border-amber-300">Ineligible</Badge>}
                </div>
                <p className="text-[11px] font-semibold text-slate-800 mt-1.5 leading-snug">{p.reference}</p>
                {p.passage_type === "AI-generated paraphrase" ? (
                  <div className="border border-rose-200 bg-rose-50/60 rounded px-2.5 py-1.5 mt-1.5">
                    <p className="font-mono text-[8px] uppercase tracking-widest text-rose-600">AI-generated paraphrase — not a verbatim quotation</p>
                    <p className="text-[11px] text-slate-700 leading-snug">{p.passage}</p>
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-700 italic border-l-2 border-teal-400 pl-2.5 mt-1.5 leading-snug">"{p.passage}"</p>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[10px] text-slate-500">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.locator}</span>
                  <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" />{p.quality_status}</span>
                  <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" />{p.reviewer}</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5"><span className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 mr-1">Applicability</span>{p.applicability_note}</p>
                {p.ineligible_reason && <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{p.ineligible_reason}</p>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
